import React, { useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SWRConfig } from 'swr';
import { AuthContext, AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import NotificationContainer from './components/NotificationContainer';
import Login from './pages/Login';
import Register from './pages/Register';
import Feed from './pages/Feed';
import Jobs from './pages/Jobs';
import Network from './pages/Network';
import Profile from './pages/Profile';
import Showcase from './pages/Showcase';
import EnhancedProfile from './pages/EnhancedProfile';
import ProfileSettings from './pages/ProfileSettings';
import Messages from './pages/Messages';
import Landing from './pages/Landing';
import Onboarding from './pages/Onboarding';
import Production from './pages/Production';
import Saved from './pages/Saved';
import Navbar from './components/Navbar';
import './App.css';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <div className="error-boundary-card">
            <h1 className="error-boundary-title">Something went wrong</h1>
            <p className="error-boundary-message">Please refresh the page or try again later.</p>
            <button
              type="button"
              className="error-boundary-button"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function PrivateRoute({ children }) {
  const { user, loading } = useContext(AuthContext);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return user ? children : <Navigate to="/" />;
}

function PublicRoute({ children }) {
  const { user, loading } = useContext(AuthContext);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) return children;

  if (user.onboarding_complete === false) return children;

  return <Navigate to="/feed" />;
}

function AppRoutes() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const knownRoutes = [
    '/',
    '/login',
    '/register',
    '/feed',
    '/network',
    '/jobs',
    '/messages',
    '/showcase',
    '/profile-settings',
    '/onboarding',
    '/production',
    '/saved',
  ];
  const isSlugRoute =
    location.pathname.match(/^\/[a-z0-9_-]+$/i) &&
    !knownRoutes.includes(location.pathname) &&
    !location.pathname.startsWith('/profile') &&
    !location.pathname.startsWith('/showcase');
  const isShowcase = location.pathname.includes('/showcase') || isSlugRoute;
  const isLanding = location.pathname === '/' && !user;
  // SHO-40: keep the navbar visible on /onboarding so users who logged
  // back in mid-setup can still log out or navigate — matching the
  // behavior of /register's setup steps, which already render the navbar.
  const hideNavbar = isShowcase;

  return (
    <div className="App">
      {user && !hideNavbar && <Navbar />}
      <div className={user && !hideNavbar && !isLanding ? 'main-content' : ''}>
        <Routes>
          <Route path="/" element={!user ? <Landing /> : <Navigate to="/feed" />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <PrivateRoute>
                <Onboarding />
              </PrivateRoute>
            }
          />
          <Route
            path="/feed"
            element={
              <PrivateRoute>
                <Feed />
              </PrivateRoute>
            }
          />
          <Route
            path="/network"
            element={
              <PrivateRoute>
                <Network />
              </PrivateRoute>
            }
          />
          <Route
            path="/jobs"
            element={
              <PrivateRoute>
                <Jobs />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile/:userId"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile-settings"
            element={
              <PrivateRoute>
                <ProfileSettings />
              </PrivateRoute>
            }
          />
          <Route
            path="/showcase"
            element={
              <PrivateRoute>
                <Showcase />
              </PrivateRoute>
            }
          />
          <Route path="/showcase/user/:userId" element={<Showcase />} />
          <Route path="/showcase/demo" element={<EnhancedProfile />} />
          <Route
            path="/production"
            element={
              <PrivateRoute>
                <Production />
              </PrivateRoute>
            }
          />
          <Route
            path="/saved"
            element={
              <PrivateRoute>
                <Saved />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <PrivateRoute>
                <Messages />
              </PrivateRoute>
            }
          />
          <Route
            path="/messages/:userId"
            element={
              <PrivateRoute>
                <Messages />
              </PrivateRoute>
            }
          />
          {/* Public showcase by slug — catch-all must be last */}
          <Route path="/:slug" element={<Showcase />} />
        </Routes>
      </div>
    </div>
  );
}

function AppContent() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <SWRConfig value={{ revalidateOnFocus: false, shouldRetryOnError: false }}>
        <NotificationProvider>
          <NotificationContainer />
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </NotificationProvider>
      </SWRConfig>
    </ErrorBoundary>
  );
}

export default App;
